import { Dispatch, SetStateAction, useCallback } from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import { ExperimentType } from "../../Types";
import {
  fetchAdoJsonContent,
  fetchExperimentsInOpenPrs,
  fetchExtensionExperiments,
  fetchPipelineConfigOpenPrs,
} from "../../Api/ado.api";
import { useEv2ExperimentParams } from "../UseCurrentExtension";
import { ARCHIVE_KEY, UNARCHIVE_KEY } from "../../Shared/shared.consts";
import { useAppContext } from "../../Contexts";

export function useFetchExperiments(setError?: Dispatch<SetStateAction<any>>) {
  const { setError: appSetError } = useAppContext();
  const { extensionFolder } = useEv2ExperimentParams();
  const setErrorFunc = setError || appSetError;
  const func = useCallback(
    async (_disposable) => {
      if (!extensionFolder) {
        return { experiments: [], archivedExperiments: [] };
      }
      const extensionRepoPath = extensionFolder.extensionRepoPath;
      const files = await fetchExtensionExperiments(extensionRepoPath);

      if (!files) {
        setErrorFunc("Failed to fetch extensions");
        return { experiments: undefined, archivedExperiments: undefined };
      }

      const filteredFiles = files.filter(
        (folder) =>
          !folder.isFolder &&
          folder.path.endsWith(".json") &&
          !folder.path.includes("ConfigurationSpec_ABExperiment.json") &&
          !folder.path.includes("/Templates/") &&
          !folder.path.includes(".pipelines") &&
          !folder.path.includes("ConfigurationSpec_Policy_Template.json") &&
          !folder.path.includes("ConfigurationSpec_Template.json") &&
          !folder.path.includes("base.json"),
      );

      const repoExperiments = await Promise.all(
        filteredFiles.map(async (file) => {
          const content = (await fetchAdoJsonContent(file)) as ExperimentType;
          return {
            lastModifiedBy: file.lastModifiedBy,
            lastModifiedDate: file.lastModifiedDate,
            ...content,
          };
        }),
      );
      const pullRequestExperiments =
        await fetchExperimentsInOpenPrs(extensionRepoPath);
      const uniqueExperiments = mergeExperiments(
        repoExperiments,
        pullRequestExperiments,
      );
      const sortedExperiments = uniqueExperiments
        .filter(Boolean)
        .sort((a, b) => {
          const dateA = a.lastModifiedDate
            ? new Date(a.lastModifiedDate).getTime()
            : 0;
          const dateB = b.lastModifiedDate
            ? new Date(b.lastModifiedDate).getTime()
            : 0;
          return dateB - dateA;
        });
      const experiments = sortedExperiments.filter(
        (e) => !e.__fullpath?.includes(ARCHIVE_KEY),
      );
      const archivedExperiments = sortedExperiments.filter((e) =>
        e.__fullpath?.includes(ARCHIVE_KEY),
      );

      return { experiments, archivedExperiments };
    },
    [extensionFolder, setErrorFunc],
  );
  return useAsyncResult({
    func,
    setError: setErrorFunc,
  });
}

export function useFetchPipelineConfigOpenPrs(
  setError: Dispatch<SetStateAction<any>>,
) {
  const func = useCallback(
    async (_disposable) => {
      try {
        const configPr = await fetchPipelineConfigOpenPrs();
        if (!configPr) {
          return null;
        }
        return configPr;
      } catch (error) {
        setError("Failed to fetch pipeline configuration PRs");
        return null;
      }
    },
    [setError],
  );

  return useAsyncResult({
    func,
    setError,
  });
}

function mergeExperiments(
  experiments: ExperimentType[],
  pullRequestExperiments: ExperimentType[],
): ExperimentType[] {
  return pullRequestExperiments.reduce(
    (merged, prExperiment) => {
      const existingIndex = merged.findIndex(
        (exp) => exp.featureKey === prExperiment.featureKey,
      );
      if (existingIndex !== -1) {
        // Merge PR experiment with existing experiment
        let mergedExperiment = {
          ...merged[existingIndex],
          ...prExperiment,
        };
        // We want to keep the original experiment data for archive/unarchive operations
        // and only keep the pr related fields from the PR experiment
        if (
          prExperiment.__filename.includes(ARCHIVE_KEY) ||
          prExperiment.pr?.title?.includes(UNARCHIVE_KEY)
        ) {
          mergedExperiment = {
            ...merged[existingIndex],
            prUrl: prExperiment.prUrl,
            pr: prExperiment.pr,
            lastModifiedBy: prExperiment.lastModifiedBy,
            lastModifiedDate: prExperiment.lastModifiedDate,
          };
        }
        merged[existingIndex] = mergedExperiment;
      } else {
        // Add new PR experiment
        merged.push({
          ...prExperiment,
          isNew: true,
        });
      }

      return merged;
    },
    [...experiments],
  );
}
